import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth-context';
import GlobalSearch from '../GlobalSearch';
import {
  Building2,
  Users,
  Shield,
  FileText,
  LayoutDashboard,
  Hotel,
  BedDouble,
  Menu,
  X,
  LogOut,
  ChevronDown,
  Settings,
  Moon,
  Sun,
  CalendarDays,
  UserCircle,
  DollarSign,
  CalendarCheck,
  ClipboardList,
  Receipt,
  Grid3X3,
  Sparkles,
  PieChart,
  UtensilsCrossed,
  Wrench,
  Globe,
  Heart,
  Flower2,
  Calendar,
  Package,
  BarChart3,
  Blocks,
  Zap,
  Languages,
  GitBranch,
  ShieldAlert,
  ClipboardCheck,
  Box,
  ChefHat,
  BookOpen,
  Wallet,
  Boxes,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';

const navSections = [
  {
    section: 'Overview',
    items: [
      { name: 'Dashboard', href: '/admin', icon: LayoutDashboard, permission: 'property:read' },
      { name: 'Analytics', href: '/admin/analytics', icon: BarChart3, permission: 'analytics:read' },
      { name: 'Reports', href: '/admin/reports', icon: FileText, permission: 'report:read' },
    ],
  },
  {
    section: 'Front Office',
    items: [
      { name: 'Front Desk', href: '/admin/front-desk', icon: ClipboardList, permission: 'reservation:read' },
      { name: 'Reservations', href: '/admin/reservations', icon: CalendarDays, permission: 'reservation:read' },
      { name: 'Availability', href: '/admin/availability', icon: CalendarCheck, permission: 'availability:read' },
      { name: 'Room Status', href: '/admin/room-status', icon: Grid3X3, permission: 'room:read' },
      { name: 'Guests', href: '/admin/guests', icon: UserCircle, permission: 'guest:read' },
    ],
  },
  {
    section: 'Sales & Revenue',
    items: [
      { name: 'Rates', href: '/admin/rates', icon: DollarSign, permission: 'rate:read' },
      { name: 'Distribution', href: '/admin/distribution', icon: Globe, permission: 'channel:read' },
      { name: 'CRM & Loyalty', href: '/admin/crm', icon: Heart, permission: 'loyalty:read' },
      { name: 'Events', href: '/admin/events', icon: Calendar, permission: 'event:read' },
    ],
  },
  {
    section: 'Guest Experience',
    items: [
      { name: 'Concierge', href: '/admin/concierge', icon: Heart, permission: 'concierge:read' },
      { name: 'Spa', href: '/admin/spa', icon: Flower2, permission: 'spa:read' },
      { name: 'Smart Room', href: '/admin/smart-room', icon: Sparkles, permission: 'smartroom:read' },
      { name: 'Surveys', href: '/admin/surveys', icon: ClipboardList, permission: 'guest:read' },
    ],
  },
  {
    section: 'Operations',
    items: [
      { name: 'Housekeeping', href: '/admin/housekeeping', icon: Sparkles, permission: 'housekeeping:read' },
      { name: 'Maintenance', href: '/admin/maintenance', icon: Wrench, permission: 'maintenance:read' },
      { name: 'Workforce', href: '/admin/workforce', icon: Users, permission: 'employee:read' },
      { name: 'Energy', href: '/admin/energy', icon: Zap, permission: 'energy:read' },
      { name: 'Outsourcing', href: '/admin/outsourcing', icon: Hotel, permission: 'reservation:read' },
    ],
  },
  {
    section: 'Inventory & Procurement',
    items: [
      { name: 'Inventory', href: '/admin/inventory', icon: Box, permission: 'procurement:read' },
      { name: 'Procurement', href: '/admin/procurement', icon: Package, permission: 'procurement:read' },
      { name: 'F&B Cost Control', href: '/admin/fnb-cost-control', icon: ChefHat, permission: 'procurement:read' },
      { name: 'Stores & Expiry', href: '/admin/stores', icon: Boxes, permission: 'procurement:read' },
    ],
  },
  {
    section: 'Finance',
    items: [
      { name: 'Folios', href: '/admin/folios', icon: Receipt, permission: 'folio:read' },
      { name: 'Finance', href: '/admin/finance', icon: PieChart, permission: 'invoice:read' },
      { name: 'Accounting', href: '/admin/accounting', icon: BookOpen, permission: 'invoice:read' },
      { name: 'Credit & Deposits', href: '/admin/credit', icon: Wallet, permission: 'invoice:read' },
      { name: 'Night Audit', href: '/admin/night-audit', icon: Moon, permission: 'invoice:read' },
    ],
  },
  {
    section: 'Administration',
    items: [
      { name: 'Properties', href: '/admin/properties', icon: Building2, permission: 'property:read' },
      { name: 'Rooms', href: '/admin/rooms', icon: BedDouble, permission: 'room:read' },
      { name: 'Portfolio', href: '/admin/portfolio', icon: GitBranch, permission: 'portfolio:read' },
      { name: 'Users', href: '/admin/users', icon: Users, permission: 'user:read' },
      { name: 'Roles', href: '/admin/roles', icon: Shield, permission: 'role:read' },
      { name: 'Hotel Registrations', href: '/admin/registrations', icon: ClipboardCheck, permission: 'role:read' },
      { name: 'Integrations', href: '/admin/integrations', icon: Blocks, permission: 'integration:read' },
      { name: 'Localization', href: '/admin/localization', icon: Languages, permission: 'localization:read' },
      { name: 'Disaster Recovery', href: '/admin/disaster-recovery', icon: ShieldAlert, permission: 'dr:read' },
      { name: 'Audit Logs', href: '/admin/audit', icon: FileText, permission: 'audit:read' },
    ],
  },
];

// Simplified, training-center-friendly navigation (Phase A).
// Reuses existing routes with clearer labels; hides hotel-only modules.
const trainingCenterNavSections = [
  {
    section: 'Overview',
    items: [
      { name: 'Dashboard', href: '/admin', icon: LayoutDashboard, permission: 'property:read' },
      { name: 'Analytics', href: '/admin/analytics', icon: BarChart3, permission: 'analytics:read' },
      { name: 'Reports', href: '/admin/reports', icon: FileText, permission: 'report:read' },
    ],
  },
  {
    section: 'Bookings',
    items: [
      { name: 'Venue Bookings', href: '/admin/venue-bookings', icon: Calendar, permission: 'event:read' },
      { name: 'Lodging', href: '/admin/reservations', icon: CalendarDays, permission: 'reservation:read' },
      { name: 'Availability', href: '/admin/availability', icon: CalendarCheck, permission: 'availability:read' },
      { name: 'Facilities', href: '/admin/room-status', icon: Grid3X3, permission: 'room:read' },
    ],
  },
  {
    section: 'Sales & Revenue',
    items: [
      { name: 'Rates', href: '/admin/rates', icon: DollarSign, permission: 'rate:read' },
      { name: 'Distribution', href: '/admin/distribution', icon: Globe, permission: 'channel:read' },
      { name: 'CRM & Loyalty', href: '/admin/crm', icon: Heart, permission: 'loyalty:read' },
      { name: 'Surveys', href: '/admin/surveys', icon: ClipboardList, permission: 'guest:read' },
    ],
  },
  {
    section: 'Guest Experience',
    items: [
      { name: 'Concierge', href: '/admin/concierge', icon: Heart, permission: 'concierge:read' },
      { name: 'Spa', href: '/admin/spa', icon: Flower2, permission: 'spa:read' },
      { name: 'Smart Room', href: '/admin/smart-room', icon: Sparkles, permission: 'smartroom:read' },
    ],
  },
  {
    section: 'Clients & Billing',
    items: [
      { name: 'Clients', href: '/admin/guests', icon: UserCircle, permission: 'guest:read' },
      { name: 'Credit & Deposits', href: '/admin/credit', icon: Wallet, permission: 'invoice:read' },
      { name: 'Folios', href: '/admin/folios', icon: Receipt, permission: 'folio:read' },
      { name: 'Finance', href: '/admin/finance', icon: PieChart, permission: 'invoice:read' },
      { name: 'Accounting', href: '/admin/accounting', icon: BookOpen, permission: 'invoice:read' },
      { name: 'Night Audit', href: '/admin/night-audit', icon: Moon, permission: 'invoice:read' },
    ],
  },
  {
    section: 'Catering & Stores',
    items: [
      { name: 'Catering', href: '/admin/fnb-cost-control', icon: ChefHat, permission: 'procurement:read' },
      { name: 'F&B / POS', href: '/admin/pos', icon: UtensilsCrossed, permission: 'pos:read' },
      { name: 'Inventory', href: '/admin/inventory', icon: Box, permission: 'procurement:read' },
      { name: 'Stores & Expiry', href: '/admin/stores', icon: Boxes, permission: 'procurement:read' },
      { name: 'Procurement', href: '/admin/procurement', icon: Package, permission: 'procurement:read' },
    ],
  },
  {
    section: 'Operations',
    items: [
      { name: 'Housekeeping', href: '/admin/housekeeping', icon: Sparkles, permission: 'housekeeping:read' },
      { name: 'Maintenance', href: '/admin/maintenance', icon: Wrench, permission: 'maintenance:read' },
      { name: 'Workforce', href: '/admin/workforce', icon: Users, permission: 'employee:read' },
      { name: 'Outsourcing', href: '/admin/outsourcing', icon: Hotel, permission: 'reservation:read' },
    ],
  },
  {
    section: 'Setup',
    items: [
      { name: 'Properties', href: '/admin/properties', icon: Building2, permission: 'property:read' },
      { name: 'Rooms', href: '/admin/rooms', icon: BedDouble, permission: 'room:read' },
      { name: 'Users', href: '/admin/users', icon: Users, permission: 'user:read' },
      { name: 'Roles', href: '/admin/roles', icon: Shield, permission: 'role:read' },
    ],
  },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const { user, tenant, currentProperty, properties, logout, switchProperty, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const toggleTheme = () => {
    setIsDark(!isDark);
    document.documentElement.classList.toggle('dark');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isTrainingCenter = currentProperty?.business_type === 'TRAINING_CENTER';
  const sections = isTrainingCenter ? trainingCenterNavSections : navSections;

  const renderNavItem = (item) => {
    const isActive = location.pathname === item.href ||
      (item.href !== '/admin' && location.pathname.startsWith(item.href));
    return (
      <Link
        key={item.name}
        to={item.href}
        onClick={() => setSidebarOpen(false)}
        className={cn(
          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        )}
        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '')}`}
      >
        <item.icon className="h-5 w-5 shrink-0" />
        {item.name}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setSidebarOpen(false)}>
          <div className="fixed inset-0 bg-black/50" />
        </div>
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-card border-r transform transition-transform duration-200 lg:translate-x-0 flex flex-col',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-16 items-center justify-between px-4 border-b shrink-0">
          <Link to="/admin" className="flex items-center gap-2">
            <Hotel className="h-8 w-8 text-primary" />
            <div className="flex flex-col leading-none">
              <span className="font-bold text-lg">HMS</span>
              {isTrainingCenter && (
                <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">Training Center</span>
              )}
            </div>
          </Link>
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(false)}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
          {sections.map((group) => {
            const visibleItems = group.items.filter((item) => hasPermission(item.permission));
            if (visibleItems.length === 0) return null;
            return (
              <div key={group.section} className="space-y-1">
                <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {group.section}
                </p>
                {visibleItems.map(renderNavItem)}
              </div>
            );
          })}
        </nav>

        <div className="shrink-0 p-4 border-t bg-card">
          <div className="text-xs text-muted-foreground mb-2">
            {tenant?.name}
          </div>
          <div className="text-sm font-medium truncate">
            {user?.full_name}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {user?.email}
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-card border-b flex items-center justify-between px-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Property Switcher */}
            {properties.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" className="gap-2" data-testid="property-switcher">
                    <Building2 className="h-4 w-4" />
                    <span className="max-w-[150px] truncate">{currentProperty?.name || 'Select Property'}</span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel>Switch Property</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {properties.map((prop) => (
                    <DropdownMenuItem
                      key={prop.id}
                      onClick={() => switchProperty(prop)}
                      className={cn(
                        'cursor-pointer',
                        currentProperty?.id === prop.id && 'bg-primary/10'
                      )}
                      data-testid={`property-option-${prop.code}`}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{prop.name}</span>
                        <span className="text-xs text-muted-foreground">{prop.address_city}</span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <GlobalSearch />
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="user-menu">
                  <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                    {user?.full_name?.charAt(0) || 'U'}
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{user?.full_name}</span>
                    <span className="text-xs text-muted-foreground font-normal">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
